import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AssessmentSummaryEntity } from '../../../../../api/fetchAssessmentSummary';
import { DiagnosisSuggestions } from '../../../../../api/fetchMatchingDiagnose';
import { Actions } from '../../../../../store/actions';
import { upsertDiagnose, upsertSymptom } from '../../../../../store/actions/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import {
    getDiagnosis,
    getSymFromSa,
    getSymptoms,
} from '../../../../../store/selectors/prescriptionPad';
import { DiagnosisEntityOrSymptomsEntity } from '../../../../../store/types/prescription';
import { CHANNEL_SA, DIAGNOSIS, SELECT, SYMPTOM, track } from '../../../../../tracking';
import { CHIPS, DIAGNOSIS_KIND, SA, SYMPTOMS_KIND } from '../../../trackSource';
import { DiagnosisIcon, SymptomsIcon } from './RowIcons';
import SyDyTableV2 from './SymDiaTableV2';
import TemplateIcons from './TemplateIcons';
import { getIsICDSuggestionsEnabled } from '../../../../../store/selectors/session';

export interface DiagnosisSuggestionsWithLocal extends DiagnosisSuggestions {
    isLocal?: boolean;
}

type CompProps = {
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND;
};

interface Props extends CompProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: DiagnosisEntityOrSymptomsEntity[];
    // relatedSym: RelatedSymDia[];
    // relatedDia: RelatedSymDia[];
    symFromSa: AssessmentSummaryEntity[];
    isICDSuggestionsEnable?: boolean;
    sectionName?: string;
}

class SymDiaV2 extends PureComponent<Props> {
    ELEMENT = this.props.kind === SYMPTOMS_KIND ? SYMPTOM : DIAGNOSIS;

    onElementSelected = (element: DiagnosisEntityOrSymptomsEntity): void => {
        const { dispatch, kind } = this.props;
        dispatch((kind === SYMPTOMS_KIND ? upsertSymptom : upsertDiagnose)(element));
    };

    onSASymptomSelected = (symptom: AssessmentSummaryEntity, index: number): void => {
        track(SELECT, { element: this.ELEMENT, channel: CHANNEL_SA });

        const item = {
            id: symptom.mdbid,
            name: symptom.mdbname,
            properties: symptom.properties,

            track: {
                index: index,
                label: CHIPS,
                source: SA,
            },
        };
        this.onElementSelected(item);
    };

    onAllSASymptomSelected = (symptoms: AssessmentSummaryEntity[]): void => {
        track(SELECT, { element: 'all-symptom', channel: CHANNEL_SA });
        symptoms.forEach((s, index) => this.onSASymptomSelected(s, index));
    };

    render(): ReactNode {
        const { elements, symFromSa, kind, isICDSuggestionsEnable, sectionName } = this.props;
        const uSFSa = symFromSa.filter((s) => !elements.find((i) => i.id === s.mdbid));

        return (
            <div className="rounded-8 pt-8 px-16 pb-16 bg-white row focusable">
                <div className="flex items-center justify-between">
                    {kind === SYMPTOMS_KIND ? (
                        <Fragment>
                            <div className="space-x-10 flex items-center">
                                <SymptomsIcon />
                                <p className="font-700 text-16 text-neutral-black">
                                    {sectionName || kind}
                                </p>
                                <p
                                    className={`flex items-center space-x-4 py-4 px-6 text-white font-700 text-10 rounded-4`}
                                    style={{
                                        background: isICDSuggestionsEnable ? '#2188FF' : '#E8E8E8',
                                    }}
                                >
                                    <p
                                        className={`${
                                            isICDSuggestionsEnable ? '' : 'text-darwin-neutrals-400'
                                        }`}
                                    >
                                        ICD-10
                                    </p>
                                </p>
                            </div>
                            <div className="flex items-center space-x-16 mr-16">
                                <TemplateIcons
                                    pxToolKey={'symptoms'}
                                    isModal={false}
                                    withPeak={true}
                                />
                            </div>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <div className="space-x-10 flex items-center">
                                <DiagnosisIcon />
                                <p className="font-700 text-16 text-neutral-black">
                                    {sectionName || kind}
                                </p>
                                <p
                                    className={`flex items-center space-x-4 py-4 px-6 text-white font-700 text-10 rounded-4`}
                                    style={{
                                        background: isICDSuggestionsEnable ? '#8A63D2' : '#E8E8E8',
                                    }}
                                >
                                    <p
                                        className={`${
                                            isICDSuggestionsEnable ? '' : 'text-darwin-neutrals-400'
                                        }`}
                                    >
                                        ICD-10
                                    </p>
                                </p>
                            </div>
                            <div className="flex items-center space-x-16 mr-16">
                                <TemplateIcons
                                    pxToolKey={'diagnosis'}
                                    isModal={false}
                                    withPeak={true}
                                />
                            </div>
                        </Fragment>
                    )}
                </div>
                {kind === SYMPTOMS_KIND && uSFSa.length > 0 && (
                    <div className="mt-8 flex items-baseline justify-between">
                        <div className="flex items-baseline space-x-8 w-full">
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg
                                    className="w-16 h-16 fill-secondary"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 576 512"
                                >
                                    <path d="M316.7 17.8l65.43 132.4l146.4 21.29c26.27 3.796 36.79 36.09 17.75 54.59l-105.9 102.1l25.05 145.5c4.508 26.31-23.23 45.9-46.49 33.7L288 439.6l-130.9 68.7C133.8 520.5 106.1 500.9 110.6 474.6l25.05-145.5L29.72 226.1c-19.03-18.5-8.516-50.79 17.75-54.59l146.4-21.29l65.43-132.4C271.1-6.083 305-5.786 316.7 17.8z"></path>
                                </svg>
                            </div>
                            <p className="uppercase text-neutral-6 font-700 text-12 leading-4">
                                From patient
                            </p>
                            <div className="flex-wrap flex items-center -mt-8 w-80p">
                                {uSFSa.map((us, index) => (
                                    <div
                                        key={us.mdbid}
                                        onClick={() => this.onSASymptomSelected(us, index)}
                                        className="px-12 py-4 rounded-24 cursor-pointer text-13 border border-darwin-accent-symptoms-blue-500 text-darwin-accent-symptoms-blue-500 whitespace-nowrap ml-8 mt-8 bg-darwin-accent-symptoms-blue-100"
                                    >
                                        {us.mdbname}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={() => this.onAllSASymptomSelected(uSFSa)}
                            className="derived-btn p-6 rounded-4 btn--xsmall whitespace-nowrap"
                        >
                            <div className="flex items-center justify-center w-14 h-14 mr-4">
                                <svg
                                    className="w-14 h-14 fill-actionPrimary"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path d="M432 256C432 269.3 421.3 280 408 280h-160v160c0 13.25-10.75 24.01-24 24.01S200 453.3 200 440v-160h-160c-13.25 0-24-10.74-24-23.99C16 242.8 26.75 232 40 232h160v-160c0-13.25 10.75-23.99 24-23.99S248 58.75 248 72v160h160C421.3 232 432 242.8 432 256z"></path>
                                </svg>
                            </div>
                            <p>Add All</p>
                        </button>
                    </div>
                )}
                <SyDyTableV2 kind={kind} />
            </div>
        );
    }
}

export default connect((state: GlobalState, props: CompProps) => ({
    elements: props.kind === SYMPTOMS_KIND ? getSymptoms(state) : getDiagnosis(state),
    symFromSa: props.kind === SYMPTOMS_KIND ? getSymFromSa(state) : [],
    isICDSuggestionsEnable: getIsICDSuggestionsEnabled(state),
}))(SymDiaV2);
